import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminRoofMaterial } from "../../../../../http/patrol/types/roofMaterials";
import { useRoofMaterialsApi } from "../../../../../http/patrol/useRoofMaterials";
import { PaginationQuery } from "../../../../../types";
import { formatBoolean } from "../../../../../utils";
import Button from "../../../../ui/Button";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import NewRoofMaterial from "./NewRoofMaterial";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const RoofMaterials: FC<Props> = ({ active }) => {
  const { systemId } = useParams<{ systemId: string }>();
  const i18n = useI18n();
  const navigate = useNavigate();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "roofMaterials" });
  const aborter = useRef(new AbortController());
  const roofMaterialsApi = useRoofMaterialsApi();
  const { setActions, setTitle } = useSearchContext();

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await roofMaterialsApi.updateAdminRoofMaterialVisibility(id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (roofMaterialId: string, newOrder: number) => {
    try {
      // assume API supports partial update for order
      await roofMaterialsApi.updateAdminRoofMaterialOrder(roofMaterialId, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const [showNewItemDialog, setShowNewItemDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<AdminRoofMaterial>();
  const { accessesAction, accessesDialog } = useEntityAccesses<AdminRoofMaterial>("patrol.roof_material");
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "roofMaterials");

  const isActiveTab = active ?? true;

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = roofMaterialsApi.useAdminRoofMaterials(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.roofMaterials.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.roofMaterials.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <Button buttonProps={{ onClick: () => setShowNewItemDialog(true) }}>{i18n.translation.common.createNew}</Button>
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<AdminRoofMaterial>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.name}</th>
            <th align="left">{i18n.translation.common.sheetMaterialRequired}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link onClick={(e) => e.stopPropagation()} to={`/admin/systems/${systemId}/roof_materials/${item.id}`}>
                {item.name}
              </Link>
            </td>
            <td>{formatBoolean(i18n.translation, item.sheetMaterialRequired)}</td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.id, value)} />
            </td>
            <td align="center">
              <InlineOrderEditor order={item.order} onUpdate={(newOrder) => updateOrder(item.id, newOrder)} />
            </td>
          </>
        )}
        onRowClick={(item) => navigate(`/admin/systems/${systemId}/roof_materials/${item.id}`)}
        rowActions={(item) => [
          getEditAction(i18n, () => navigate(`/admin/systems/${systemId}/roof_materials/${item.id}`)),
          accessesAction(item),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}
      {showNewItemDialog && (
        <ModalDialog onClose={() => setShowNewItemDialog(false)} title={i18n.translation.roofMaterials.createNew}>
          <NewRoofMaterial />
        </ModalDialog>
      )}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.roofMaterials.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                roofMaterialsApi
                  .deleteAdminRoofMaterial(itemToDelete.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.roofMaterials.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default RoofMaterials;
